import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle, RefreshCw, Smartphone, Search, X, Bug, Wifi, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ERROR_TYPE_LABELS = {
  exception: { label: 'Exception', color: 'bg-red-500/20 text-red-200 border-red-500/40', icon: Bug },
  flutter: { label: 'Flutter', color: 'bg-orange-500/20 text-orange-200 border-orange-500/40', icon: Layers },
  http: { label: 'HTTP', color: 'bg-amber-500/20 text-amber-200 border-amber-500/40', icon: Wifi },
};

const PLATFORM_LABELS = {
  ios: { label: 'iOS', color: 'bg-blue-500/15 text-blue-200 border-blue-500/40' },
  android: { label: 'Android', color: 'bg-green-500/15 text-green-200 border-green-500/40' },
};

function formatTimestamp(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString('de-CH', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

const MobileErrorsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [screenFilter, setScreenFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        days: String(days),
        limit: '500',
      });
      if (screenFilter) params.append('screen', screenFilter);
      if (customerFilter) params.append('customer_id', customerFilter);
      const res = await axios.get(
        `${API}/mobile/admin/mobile-error-logs?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (err) {
      console.error('Mobile errors fetch error:', err);
      toast.error('Fehler beim Laden der Mobile-Errors');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [days, screenFilter, customerFilter]);

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((it) => {
      return (
        (it.message || '').toLowerCase().includes(q) ||
        (it.screen || '').toLowerCase().includes(q) ||
        (it.http_endpoint || '').toLowerCase().includes(q) ||
        (it.customer_id || '').toLowerCase().includes(q)
      );
    });
  }, [data, search]);

  return (
    <div className="space-y-6" data-testid="mobile-errors-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            Mobile App Errors
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Live-Crash- &amp; HTTP-Fehler aus der Flutter-App (Production &amp; Preview)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-32 bg-zinc-900 border-zinc-700 text-white" data-testid="mobile-errors-days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Letzte 24 h</SelectItem>
              <SelectItem value="3">Letzte 3 Tage</SelectItem>
              <SelectItem value="7">Letzte 7 Tage</SelectItem>
              <SelectItem value="14">Letzte 14 Tage</SelectItem>
              <SelectItem value="30">Letzte 30 Tage</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="border-zinc-700 text-white"
            data-testid="mobile-errors-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardContent className="p-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Suche in Message, Endpoint, Customer …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-zinc-950 border-zinc-700 text-white"
              data-testid="mobile-errors-search-input"
            />
          </div>
          <Input
            placeholder="Screen-Filter (z.B. BookingDialog)"
            value={screenFilter}
            onChange={(e) => setScreenFilter(e.target.value)}
            className="w-64 bg-zinc-950 border-zinc-700 text-white"
            data-testid="mobile-errors-screen-filter"
          />
          <Input
            placeholder="Customer-ID"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="w-52 bg-zinc-950 border-zinc-700 text-white"
            data-testid="mobile-errors-customer-filter"
          />
          {(search || screenFilter || customerFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setScreenFilter('');
                setCustomerFilter('');
              }}
              className="text-zinc-400"
              data-testid="mobile-errors-clear-filters"
            >
              <X className="w-4 h-4 mr-1" /> Filter zurücksetzen
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Top Screens */}
      {data?.top_screens?.length > 0 && (
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-sm font-medium">
              Top-Screens mit Fehlern ({days} Tage)
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {data.top_screens.map((s) => (
              <button
                key={s.screen || 'unknown'}
                onClick={() => { setScreenFilter(s.screen || ''); }}
                className="px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm text-white border border-zinc-700 transition"
                data-testid={`mobile-errors-top-screen-${s.screen || 'unknown'}`}
              >
                <span className="text-zinc-300">{s.screen || '(unbekannt)'}</span>
                <span className="ml-2 text-red-300 font-semibold">{s.count}</span>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-48" data-testid="mobile-errors-loading">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 text-zinc-500" data-testid="mobile-errors-empty">
          <Smartphone className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p>Keine Fehler in diesem Zeitraum.</p>
          <p className="text-xs mt-1">{data?.total ?? 0} Einträge gesamt.</p>
        </div>
      ) : (
        <Card className="bg-zinc-900/60 border-zinc-800" data-testid="mobile-errors-list">
          <CardContent className="p-0 divide-y divide-zinc-800">
            {filteredItems.map((it, idx) => {
              const typeInfo = ERROR_TYPE_LABELS[it.error_type] || ERROR_TYPE_LABELS.exception;
              const platformInfo = PLATFORM_LABELS[it.platform];
              const Icon = typeInfo.icon;
              return (
                <button
                  key={idx}
                  onClick={() => setSelected(it)}
                  className="w-full text-left p-4 hover:bg-zinc-800/40 transition flex gap-4 items-start"
                  data-testid={`mobile-error-row-${idx}`}
                >
                  <div className="mt-0.5">
                    <Icon className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className={`${typeInfo.color} border text-xs`}>
                        {typeInfo.label}
                      </Badge>
                      {platformInfo && (
                        <Badge className={`${platformInfo.color} border text-xs`}>
                          {platformInfo.label}
                        </Badge>
                      )}
                      {it.screen && (
                        <span className="text-xs text-zinc-400">📍 {it.screen}</span>
                      )}
                      {it.http_status && (
                        <span className="text-xs text-zinc-400">HTTP {it.http_status}</span>
                      )}
                      {it.app_version && (
                        <span className="text-xs text-zinc-500">v{it.app_version}</span>
                      )}
                    </div>
                    <p className="text-sm text-white truncate font-mono">
                      {it.message}
                    </p>
                    {it.http_endpoint && (
                      <p className="text-xs text-zinc-500 truncate font-mono mt-0.5">
                        → {it.http_endpoint}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 whitespace-nowrap pt-1">
                    {formatTimestamp(it.timestamp)}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          className="bg-zinc-950 border-zinc-800 text-white max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="mobile-error-detail-dialog"
        >
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Mobile Error Detail
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <DetailRow label="Typ" value={ERROR_TYPE_LABELS[selected.error_type]?.label || selected.error_type} />
                  <DetailRow label="Screen" value={selected.screen || '—'} />
                  <DetailRow label="Plattform" value={selected.platform || '—'} />
                  <DetailRow label="Gerät" value={selected.device || '—'} />
                  <DetailRow label="OS" value={selected.os_version || '—'} />
                  <DetailRow label="App-Version" value={selected.app_version || '—'} />
                  <DetailRow label="Customer-ID" value={selected.customer_id || '—'} mono />
                  <DetailRow label="Timestamp" value={formatTimestamp(selected.timestamp)} />
                  {selected.http_status && (
                    <DetailRow label="HTTP-Status" value={String(selected.http_status)} />
                  )}
                  {selected.http_endpoint && (
                    <DetailRow label="Endpoint" value={selected.http_endpoint} mono />
                  )}
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Message</div>
                  <pre className="bg-zinc-900 border border-zinc-800 rounded p-3 text-red-200 text-xs font-mono whitespace-pre-wrap break-words">
                    {selected.message}
                  </pre>
                </div>

                {selected.stack_trace && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Stack-Trace</div>
                    <pre className="bg-zinc-900 border border-zinc-800 rounded p-3 text-zinc-300 text-xs font-mono whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
                      {selected.stack_trace}
                    </pre>
                  </div>
                )}

                {selected.extra && Object.keys(selected.extra).length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Extra</div>
                    <pre className="bg-zinc-900 border border-zinc-800 rounded p-3 text-zinc-300 text-xs font-mono whitespace-pre-wrap break-words">
                      {JSON.stringify(selected.extra, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-sm text-white ${mono ? 'font-mono' : ''} break-all`}>{value}</div>
  </div>
);

export default MobileErrorsPage;
